/**
 * IndexingFixTab — admin panel inside /leadboard for triaging URLs flagged
 * by search-engine indexing reports.
 *
 * Workflow:
 *   1. Paste flagged URLs (or upload the report CSV) into the input area.
 *   2. Click "Diagnose" -> backend analyzes each URL (question lookup,
 *      framing health, live HTTP probe) and auto-marks the ones that are
 *      already healthy as fixed.
 *   3. For the remaining "Open" rows, admin clicks per-row Regenerate /
 *      Reject / Won't fix. Each action persists with a timestamp.
 *   4. "Copy fixed URLs" button -> ready-to-paste list for the search-engine
 *      validation UI.
 *
 * No public surface. All endpoints are admin-only. No bridge / app.kgob.com
 * touchpoints.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  RefreshCw,
  Upload,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Copy,
  Globe,
  FileText,
  Ban,
  ExternalLink,
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Friendly labels for the issue codes returned by the backend
const ISSUE_LABELS = {
  invalid_url_shape: 'URL not in /get-your-answer/* shape',
  question_not_found: 'No matching question in library',
  question_not_published: 'Question is not published',
  no_framing: 'No framing paragraph yet',
  short_framing: 'Framing is too short',
  scope_fail: 'Framing fails content checks',
  noindex_meta: 'Page has noindex meta',
  canonical_drift: 'Canonical points elsewhere',
  live_404: 'Page returns 404',
  live_410: 'Page returns 410 (gone)',
  live_5xx: 'Page returns server error',
  probe_timeout: 'Live probe timed out',
};

const ISSUE_COLOR = {
  invalid_url_shape: 'bg-gray-700 text-gray-300 border-gray-600',
  question_not_found: 'bg-amber-500/10 text-amber-300 border-amber-500/40',
  question_not_published: 'bg-amber-500/10 text-amber-300 border-amber-500/40',
  no_framing: 'bg-red-500/10 text-red-300 border-red-500/40',
  short_framing: 'bg-orange-500/10 text-orange-300 border-orange-500/40',
  scope_fail: 'bg-red-500/10 text-red-300 border-red-500/40',
  noindex_meta: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/40',
  canonical_drift: 'bg-yellow-500/10 text-yellow-300 border-yellow-500/40',
  live_404: 'bg-red-500/10 text-red-300 border-red-500/40',
  live_5xx: 'bg-red-500/10 text-red-300 border-red-500/40',
};

const REPORT_TYPES = [
  { value: 'crawled_not_indexed', label: 'Crawled — currently not indexed' },
  { value: 'soft_404',            label: 'Soft 404' },
  { value: 'discovered_not_indexed', label: 'Discovered — currently not indexed' },
  { value: 'duplicate',           label: 'Duplicate content' },
  { value: 'other',               label: 'Other' },
];

function StatusBadge({ status }) {
  const map = {
    open:      { cls: 'bg-red-500/10 text-red-300 border-red-500/40',          label: 'OPEN' },
    fixed:     { cls: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40', label: 'FIXED' },
    rejected:  { cls: 'bg-gray-700 text-gray-300 border-gray-600',             label: 'REJECTED' },
    wont_fix:  { cls: 'bg-gray-700 text-gray-300 border-gray-600',             label: "WON'T FIX" },
  };
  const v = map[status] || map.open;
  return (
    <span className={`text-[10px] tracking-[0.14em] uppercase font-semibold border px-1.5 py-0.5 rounded ${v.cls}`}>
      {v.label}
    </span>
  );
}

export default function IndexingFixTab({ sessionToken }) {
  const [rawInput, setRawInput] = useState('');
  const [reportType, setReportType] = useState('crawled_not_indexed');
  const [diagnosing, setDiagnosing] = useState(false);
  const [filter, setFilter] = useState('open');
  const [jobs, setJobs] = useState([]);
  const [counts, setCounts] = useState({ open: 0, fixed: 0, rejected: 0, wont_fix: 0 });
  const [byIssue, setByIssue] = useState([]);
  const [rowState, setRowState] = useState({});
  const [rowNote, setRowNote] = useState({});
  const [lastBatch, setLastBatch] = useState(null);
  const [error, setError] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${sessionToken}`,
  }), [sessionToken]);

  const loadJobs = useCallback(async (status = filter) => {
    try {
      const r = await fetch(
        `${BACKEND}/api/v1/admin/get-your-answer/indexing-fix/jobs?status_filter=${status}&limit=300`,
        { headers: authHeaders },
      );
      const data = await r.json();
      setJobs(data.items || []);
      setCounts(data.counts || { open: 0, fixed: 0, rejected: 0, wont_fix: 0 });
      setByIssue(data.by_issue || []);
    } catch (e) {
      setError(String(e));
    }
  }, [authHeaders, filter]);

  useEffect(() => { loadJobs(filter); }, [filter, loadJobs]);

  const onCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawInput((cur) => (cur ? cur + '\n' : '') + (ev.target?.result || ''));
    reader.readAsText(file);
  };

  const diagnose = async () => {
    if (!rawInput.trim()) return;
    setDiagnosing(true);
    setError(null);
    try {
      const r = await fetch(`${BACKEND}/api/v1/admin/get-your-answer/indexing-fix/submit`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ raw_input: rawInput, report_type: reportType }),
      });
      const data = await r.json();
      if (data.error === 'no_urls_parsed') {
        setError('No URLs could be parsed from that input. Paste URLs one per line, or upload a CSV with a URL column.');
      } else {
        setLastBatch(data.batch_id);
        setRawInput('');
        await loadJobs(filter);
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setDiagnosing(false);
    }
  };

  const rowAction = async (jobId, action, note = '') => {
    setRowState((s) => ({ ...s, [jobId]: action }));
    try {
      const r = await fetch(
        `${BACKEND}/api/v1/admin/get-your-answer/indexing-fix/jobs/${encodeURIComponent(jobId)}/${action}`,
        {
          method: 'POST',
          headers: authHeaders,
          body: JSON.stringify({ note }),
        },
      );
      const data = await r.json();
      if (!r.ok || (data && data.success === false)) {
        throw new Error(data.detail || data.reason || `HTTP ${r.status}`);
      }
      // Refresh list — keeps the user on the same filter
      await loadJobs(filter);
    } catch (e) {
      setRowState((s) => ({ ...s, [jobId]: `error: ${String(e)}` }));
      return;
    }
    setRowState((s) => {
      const next = { ...s };
      delete next[jobId];
      return next;
    });
  };

  const copyFixedUrls = async () => {
    setCopyStatus('loading');
    try {
      const r = await fetch(`${BACKEND}/api/v1/admin/get-your-answer/indexing-fix/export`, {
        headers: authHeaders,
      });
      const data = await r.json();
      const text = (data.urls || []).join('\n');
      if (!text) {
        setCopyStatus('empty');
        setTimeout(() => setCopyStatus(''), 2000);
        return;
      }
      await navigator.clipboard.writeText(text);
      setCopyStatus(`copied ${data.count}`);
      setTimeout(() => setCopyStatus(''), 3000);
    } catch (e) {
      setCopyStatus(`error: ${String(e)}`);
      setTimeout(() => setCopyStatus(''), 3000);
    }
  };

  const openCount = counts.open || 0;
  const fixedCount = counts.fixed || 0;
  const totalCount = openCount + fixedCount + (counts.rejected || 0) + (counts.wont_fix || 0);

  return (
    <div className="space-y-6" data-testid="indexing-fix-tab">
      {/* Header */}
      <Card className="bg-navy-light border-gray-700">
        <CardContent className="pt-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-ivory text-xl font-semibold flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#D4AF37]" />
                Indexing
              </h2>
              <p className="text-xs text-gray-400 mt-1 max-w-2xl">
                Paste URLs flagged by search-engine indexing reports below. Each URL is matched against the library, content health is checked, and the live page is probed. URLs that are already healthy auto-mark as fixed — no action needed.
              </p>
            </div>
            <div className="flex gap-3 text-xs shrink-0">
              <span className="px-2.5 py-1 rounded bg-red-500/10 text-red-300 border border-red-500/30">
                {openCount} open
              </span>
              <span className="px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">
                {fixedCount} fixed
              </span>
              <span className="px-2.5 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700">
                {totalCount} total
              </span>
            </div>
          </div>

          {/* Input area */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="bg-navy border-gray-700 border text-ivory text-sm rounded px-3 py-1.5"
                data-testid="indexing-report-type"
                disabled={diagnosing}
              >
                {REPORT_TYPES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
              <label className="text-xs text-gray-400 cursor-pointer hover:text-ivory flex items-center gap-1.5">
                <Upload className="w-3.5 h-3.5" />
                Upload CSV
                <input
                  type="file"
                  accept=".csv,text/csv,text/plain"
                  onChange={onCsvFile}
                  className="hidden"
                  data-testid="indexing-csv-upload"
                  disabled={diagnosing}
                />
              </label>
            </div>
            <Textarea
              value={rawInput}
              onChange={(e) => setRawInput(e.target.value)}
              placeholder="Paste flagged URLs, one per line. CSV from the indexing report also works — headers and quotes are tolerated."
              rows={6}
              className="bg-navy border-gray-700 text-ivory text-xs font-mono"
              data-testid="indexing-paste-box"
              disabled={diagnosing}
              maxLength={200000}
            />
            <div className="flex items-center gap-3">
              <Button
                onClick={diagnose}
                disabled={diagnosing || !rawInput.trim()}
                className="bg-[#D4AF37] hover:bg-[#B99610] text-black font-semibold"
                data-testid="indexing-diagnose-btn"
              >
                {diagnosing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Diagnose
              </Button>
              <Button
                onClick={copyFixedUrls}
                variant="outline"
                className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                data-testid="indexing-export-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy fixed URLs
              </Button>
              {copyStatus && (
                <span className="text-xs text-emerald-300" data-testid="indexing-copy-status">
                  {copyStatus.startsWith('copied') ? `Copied ${copyStatus.split(' ')[1]} URL(s) to clipboard — ready to paste into the validation UI` :
                   copyStatus === 'empty' ? 'No fixed URLs yet.' :
                   copyStatus === 'loading' ? 'Loading…' :
                   copyStatus}
                </span>
              )}
              {error && (
                <span className="text-xs text-red-300 ml-2">{error}</span>
              )}
            </div>
          </div>

          {/* Filter pills */}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            {['open', 'fixed', 'rejected', 'wont_fix'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                  filter === f
                    ? 'bg-[#D4AF37] text-black border-[#D4AF37]'
                    : 'bg-navy text-gray-300 border-gray-700 hover:border-gray-500'
                }`}
                data-testid={`indexing-filter-${f}`}
              >
                {f === 'wont_fix' ? "Won't fix" : f.charAt(0).toUpperCase() + f.slice(1)}
                <span className="ml-1.5 text-[10px] opacity-70">{counts[f] || 0}</span>
              </button>
            ))}
            {byIssue.length > 0 && filter === 'open' && (
              <div className="ml-auto flex flex-wrap gap-1.5">
                {byIssue.slice(0, 6).map((b) => (
                  <span
                    key={b.issue}
                    className="text-[10px] tracking-[0.12em] uppercase text-gray-400 bg-navy border border-gray-700 px-1.5 py-0.5 rounded"
                  >
                    {b.issue}: <b className="text-red-300">{b.count}</b>
                  </span>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Job rows */}
      <Card className="bg-navy-light border-gray-700">
        <CardContent className="pt-5">
          {jobs.length === 0 ? (
            <div className="py-8 text-center text-gray-500 text-sm flex flex-col items-center gap-2">
              <FileText className="w-6 h-6 text-gray-600" />
              {filter === 'open' ? 'No open items. Paste URLs above to diagnose.' :
               filter === 'fixed' ? 'No fixed items yet.' :
               `No ${filter} items.`}
            </div>
          ) : (
            <ul className="space-y-2">
              {jobs.map((job) => {
                const state = rowState[job.id] || 'idle';
                const note = rowNote[job.id] || '';
                const issueLabel = ISSUE_LABELS[job.issue] || job.issue || 'Healthy';
                const issueColor = ISSUE_COLOR[job.issue] || 'bg-gray-700 text-gray-300 border-gray-600';
                const busy = state !== 'idle' && !state.startsWith('error');
                return (
                  <li
                    key={job.id}
                    className="p-3 rounded-lg bg-navy border border-gray-700 hover:border-gray-600 transition-colors"
                    data-testid={`indexing-row-${job.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                          <StatusBadge status={job.status} />
                          {job.issue && (
                            <span className={`text-[10px] tracking-[0.14em] uppercase font-semibold border px-1.5 py-0.5 rounded ${issueColor}`}>
                              {issueLabel}
                            </span>
                          )}
                          <span className={`text-[10px] tracking-[0.14em] uppercase font-semibold border px-1.5 py-0.5 rounded ${
                            job.http_status === 200 ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40' :
                            job.http_status === 0 ? 'bg-gray-700 text-gray-400 border-gray-600' :
                            'bg-red-500/10 text-red-300 border-red-500/40'
                          }`}>
                            HTTP {job.http_status || '—'}
                          </span>
                          {job.framing_word_count > 0 && (
                            <span className="text-[10px] tracking-[0.14em] uppercase text-gray-500">
                              {job.framing_word_count} words
                            </span>
                          )}
                          {job.topic_label && (
                            <span className="text-[10px] tracking-[0.14em] uppercase text-gray-500">
                              {job.topic_label}
                            </span>
                          )}
                          {job.validated_at && (
                            <span className="text-[10px] text-gray-600 ml-auto">
                              {new Date(job.validated_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                        <a
                          href={job.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-ivory text-xs font-mono leading-snug break-all hover:text-[#D4AF37] inline-flex items-start gap-1 group"
                          data-testid={`indexing-row-url-${job.id}`}
                        >
                          <span>{job.url}</span>
                          <ExternalLink className="w-3 h-3 mt-0.5 opacity-0 group-hover:opacity-60 shrink-0" />
                        </a>
                        {job.question_text && (
                          <p className="text-[11px] text-gray-500 mt-1">&ldquo;{job.question_text}&rdquo;</p>
                        )}
                        {job.probe_error && (
                          <p className="text-[11px] text-red-400 mt-1">probe error: {job.probe_error}</p>
                        )}
                        {job.admin_note && (
                          <p className="text-[11px] text-gray-500 mt-1 italic">note: {job.admin_note}</p>
                        )}
                      </div>
                      {job.status === 'open' && (
                        <div className="flex flex-col gap-2 items-end shrink-0">
                          <div className="flex gap-2 flex-wrap justify-end">
                            {job.question_id && (
                              <Button
                                size="sm"
                                onClick={() => rowAction(job.id, 'regenerate')}
                                disabled={busy}
                                className="bg-[#D4AF37] hover:bg-[#B99610] text-black font-semibold"
                                data-testid={`indexing-regen-${job.id}`}
                              >
                                {state === 'regenerate' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                                Regenerate
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => rowAction(job.id, 'mark-fixed', note)}
                              disabled={busy}
                              className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                              data-testid={`indexing-mark-${job.id}`}
                            >
                              {state === 'mark-fixed' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                              Mark fixed
                            </Button>
                            {job.question_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rowAction(job.id, 'reject-question', note)}
                                disabled={busy}
                                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                                data-testid={`indexing-reject-${job.id}`}
                              >
                                {state === 'reject-question' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
                                Reject
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => rowAction(job.id, 'wont-fix', note)}
                              disabled={busy}
                              className="border-gray-600 text-gray-400 hover:bg-gray-800"
                              data-testid={`indexing-wontfix-${job.id}`}
                            >
                              {state === 'wont-fix' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3 h-3 mr-1" />}
                              Won&apos;t fix
                            </Button>
                          </div>
                          <Input
                            type="text"
                            value={note}
                            onChange={(e) => setRowNote((s) => ({ ...s, [job.id]: e.target.value }))}
                            placeholder="Note (optional)"
                            className="h-7 text-xs w-48 bg-navy-light border-gray-700 text-gray-300"
                            disabled={busy}
                            maxLength={120}
                            data-testid={`indexing-note-${job.id}`}
                          />
                          {state.startsWith('error') && (
                            <span className="text-[11px] text-red-400 max-w-xs text-right">
                              {state.replace('error:', '').trim()}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
